import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './frog-view.css'
import App from './App'
import { kachelSchaerfeStarten } from './lib/kachelSchaerfe'

// Build stamp — bump on cache-poisoning incidents: changing this string
// re-hashes the entry chunk so browsers holding a poisoned same-name chunk
// (immutable-cached) are forced onto fresh filenames (2026-07-04 incident:
// a cancelled CI run overwrote a verified deploy; Safari pinned the broken
// entry for a year via Cache-Control immutable).
console.info('[Build] tschepp-ar-web 2026-07-04T16:10+02:00 r1')

// ── Global Error Handler → Log API ──────────────────────────────
const LOG_API = 'https://log-api.arkturian.com/api/v1/sessions'

async function getOrCreateErrorSession(): Promise<string> {
  const key = '__errorLogSessionId'
  const existing = (window as any)[key]
  if (existing) return existing
  const id = `tschepp-err-${Date.now()}`
  try {
    await fetch(LOG_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, app_id: 'tschepp-ar-web', device_model: navigator.userAgent.slice(0, 80), os: navigator.platform }),
    })
    ;(window as any)[key] = id
    return id
  } catch { return id }
}

async function sendErrorToLogAPI(type: string, message: string, stack?: string) {
  try {
    const sessionId = await getOrCreateErrorSession()
    await fetch(`${LOG_API}/${sessionId}/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries: [{
        timestamp: new Date().toISOString(),
        log_type: 'Error',
        source: 'global-error-handler',
        message: `[${type}] ${message}`,
        stack_trace: stack || null,
        sequence: 0,
      }] }),
    })
  } catch { /* last resort — can't log the logger */ }
}

// Benign errors we suppress from log-api so they don't drown signal in
// noise. ResizeObserver loop warnings are harmless browser instrumentation
// — they fire when an observer callback triggers layout in the same tick,
// which can happen with WebGL canvas resize chains and is fine in
// practice.
const BENIGN_ERROR_PATTERNS = [
  /ResizeObserver loop/i,
  /ResizeObserver loop completed with undelivered notifications/i,
]
function isBenignError(message: unknown): boolean {
  const text = typeof message === 'string' ? message : String(message ?? '')
  return BENIGN_ERROR_PATTERNS.some((rx) => rx.test(text))
}

window.onerror = (message, source, lineno, colno, error) => {
  if (isBenignError(message)) return
  sendErrorToLogAPI('onerror', `${message} at ${source}:${lineno}:${colno}`, error?.stack)
}

window.onunhandledrejection = (event) => {
  const reason = event.reason
  const msg = reason instanceof Error ? reason.message : String(reason)
  const stack = reason instanceof Error ? reason.stack : undefined
  sendErrorToLogAPI('unhandledrejection', msg, stack)
}

// ── App ─────────────────────────────────────────────────────────
kachelSchaerfeStarten()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
